// @givmo/board — the note an AI wrote on a proposal, kept behind one link until a person asks for it
// (CBG-UX12, Jonathan 2026-10-29 11:41 PDT): "Read the AI assistant's rationale" opens the full text
// below it, and "Close" at its foot folds it away again. Wherever Board shows that note: a proposed
// member on the roster and in the form filled from them, a proposed record on the confirmation page,
// and an imported record's own page. The read of one proposed record (frame b4) shows the note in
// full, being the place a person goes to read everything the AI wrote.
//
// Both links are the design system's own button in its link tone, so the toggle is a real button:
// Enter and Space press it, it says whether the note is open (`aria-expanded`) and which region it
// opens (`aria-controls`), and Close hands focus back to it, so a keyboard reader isn't left on a
// control that has just gone away.
//
// Where a list shows one note per row (the confirmation page's records, the roster's proposed
// members, a governing document's versions), every toggle and Close read the same, so each is
// described by its row's title (`describedBy`, `aria-describedby`), and a screen reader says which
// record or person it opens (CBG-FIX170). No visible word changes.

import { useId, useRef, useState } from 'react';
import { Button } from '@givmo/design-system';
import { proposalWords as w } from './proposal-words';

const NOTE = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';

export function AiRationale({
  note,
  testId,
  describedBy,
}: {
  note: string;
  testId: string;
  /** The id of the row's title, where several notes share a page. */
  describedBy?: string;
}) {
  const [open, setOpen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const region = useId();
  return (
    <div className="flex min-w-0 flex-col items-start gap-4" data-testid={testId}>
      <Button
        ref={toggle}
        appearance="saas"
        tone="link"
        size="sm"
        aria-expanded={open}
        aria-controls={region}
        aria-describedby={describedBy}
        onClick={() => setOpen((shown) => !shown)}
        testId={`${testId}-toggle`}
      >
        {w.readRationale}
      </Button>
      <div id={region} hidden={!open} className="flex min-w-0 flex-col items-start gap-4">
        {open ? (
          <>
            <p
              className={`${NOTE} whitespace-pre-wrap wrap-anywhere`}
              data-testid={`${testId}-text`}
            >
              {note}
            </p>
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              aria-describedby={describedBy}
              onClick={() => {
                setOpen(false);
                toggle.current?.focus();
              }}
              testId={`${testId}-close`}
            >
              {w.closeRationale}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
